import { generateUUID } from './idUtils'

/**
 * Where a session's id comes from.
 *
 * The id is six base-36 characters, and it is the whole of the gallery QR on
 * the strip (`<gallery>/<id>`). It used to be made up here with Math.random
 * and never checked. With ~570k ids already stored, about one new session in
 * 4,000 drew an id another booth held. The server refuses that session
 * (409 session_id_conflict) rather than merge two customers' photos, so the
 * guest's softfile sat in storage while their QR opened a stranger's gallery:
 * `qactnv` and `1sn6y0` on one ahbox booth, 2026-10-16 and 2026-10-18.
 *
 * The server can catch a collision but never repair it, because by the time it
 * sees the session the id is printed. So the id now comes from
 * `GET /api/sessions/next-id`, which checks it is free in sessions and
 * galleries. One is fetched AHEAD of time, so Start stays instant and every
 * caller stays synchronous: `takeSessionId()` hands over the id already held
 * and fetches the next one in the background.
 *
 * Offline, signed out, or talking to a server without that route (admin.bahela.id
 * until it is cherry-picked), it falls back to the local mint: the old
 * behaviour, so nothing gets worse.
 */

const FETCH_TIMEOUT_MS = 5_000
/**
 * A fetch that failed for a passing reason (offline, timeout, 5xx) tries again
 * this much later, so a booth that boots offline heals before its first guest.
 * A 401 or 404 waits for the next Start or sign-in instead of polling.
 */
const RETRY_AFTER_MS = 60_000
/** Anything else from the server is ignored rather than printed on a strip. */
const SESSION_ID_SHAPE = /^[0-9a-z]{6}$/

/** `retry` says whether the failure is worth trying again on a timer. */
type IdFetchResult = { id: string } | { id: null; retry: boolean }
type IdFetcher = () => Promise<IdFetchResult>

/**
 * Same identity the rest of the booth API surface sends (see featureFlagsStore):
 * the auth-store token, else the main process's copy. `authStore` is imported at
 * call time so this module never joins an import cycle.
 */
const authHeaders = async (): Promise<Record<string, string>> => {
  try {
    const { useAuthStore } = await import('@renderer/stores/authStore')
    const api = (window as unknown as { api?: { getAuthToken?: () => Promise<string | null> } }).api
    const token =
      useAuthStore.getState().user?.cookie ||
      (typeof api?.getAuthToken === 'function' ? await api.getAuthToken().catch(() => null) : null)
    return token ? { Authorization: `Bearer ${token}` } : {}
  } catch {
    // No preload (web build) or no token: the session cookie still applies.
    return {}
  }
}

const fetchFromServer: IdFetcher = async () => {
  const controller = new AbortController()
  const timer = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS)
  try {
    const res = await fetch(`${process.env.VITE_API_URL}/api/sessions/next-id`, {
      credentials: 'include',
      cache: 'no-store',
      headers: await authHeaders(),
      signal: controller.signal
    })
    if (!res.ok) return { id: null, retry: res.status >= 500 }
    const body = (await res.json()) as { id?: unknown }
    return typeof body.id === 'string' && SESSION_ID_SHAPE.test(body.id)
      ? { id: body.id }
      : { id: null, retry: false }
  } catch {
    return { id: null, retry: true }
  } finally {
    clearTimeout(timer)
  }
}

let fetcher: IdFetcher = fetchFromServer
let held: string | null = null
let inflight: Promise<void> | null = null
let retryTimer: ReturnType<typeof setTimeout> | null = null

/** Make sure a server-checked id is ready for the next Start. Never throws. */
export function prefetchSessionId(): Promise<void> {
  if (held) return Promise.resolve()
  if (inflight) return inflight
  inflight = Promise.resolve()
    .then(() => fetcher())
    .catch((): IdFetchResult => ({ id: null, retry: true }))
    .then((result) => {
      if (result.id !== null) {
        held = result.id
      } else if (result.retry && !retryTimer) {
        retryTimer = setTimeout(() => {
          retryTimer = null
          void prefetchSessionId()
        }, RETRY_AFTER_MS)
      }
    })
    .finally(() => {
      inflight = null
    })
  return inflight
}

/**
 * The id for a session that is starting now. Hands over the held id (each one
 * exactly once) or, when there is none, mints locally. Either way it starts
 * fetching the id for the session after this one.
 */
export function takeSessionId(): string {
  const id = held
  held = null
  void prefetchSessionId()
  return id ?? generateUUID()
}

/** Tests only: swap the network call and forget any held id. */
export function resetSessionIdSourceForTests(next?: IdFetcher): void {
  fetcher = next ?? fetchFromServer
  held = null
  inflight = null
  if (retryTimer) clearTimeout(retryTimer)
  retryTimer = null
}
